import { LayerKey } from '@festina-life/api'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'

import {
  configurationItem,
  scratchpad,
  server as serverPath,
} from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import type { TabLink } from './types'
import { useCurrentPathAsTabLink } from './useCurrentPathAsTabLink'

type Props = {
  visibleTabLinks: Optional<TabLink[]>
  updateTabLinks: (nextValue: TabLink[]) => void
}

export const useCloseTab = ({ visibleTabLinks, updateTabLinks }: Props) => {
  const navigate = useNavigate()
  const { block, branch, server } = useNavigationParams()
  const currentPathAsLink = useCurrentPathAsTabLink()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()

  const closeTab = useCallback(
    async (closeLink: TabLink) => {
      assertNotEmpty(visibleTabLinks)

      const closeLinkPosition = visibleTabLinks.indexOf(closeLink)

      if (closeLinkPosition !== -1) {
        const nextTabs = structuredClone(visibleTabLinks)
        let nextActiveTabLink: Optional<TabLink> = undefined

        if (closeLink.isActive) {
          if (closeLinkPosition > 0) {
            nextActiveTabLink = nextTabs[closeLinkPosition - 1]
          } else if (nextTabs.length > 1) {
            nextActiveTabLink = nextTabs[closeLinkPosition + 1]
          }
        } else {
          nextActiveTabLink = nextTabs.find((tabLink) => tabLink.isActive)
        }

        if (nextActiveTabLink && !nextActiveTabLink.isActive) {
          nextActiveTabLink.isActive = true
        }

        nextTabs.splice(closeLinkPosition, 1)
        updateTabLinks(nextTabs)

        if (!nextActiveTabLink) {
          // No tab is active while an item that does not exist is being viewed, so
          // closing another tab must not navigate away from it.
          if (!nextTabs.length) {
            await navigate(
              serverPath({
                block,
                server,
                branch,
                workspaceName: currentWorkspaceKey?.name,
              }),
            )
          }
        } else if (
          nextActiveTabLink.id !== currentPathAsLink?.id ||
          nextActiveTabLink.variant !== currentPathAsLink.variant ||
          !new LayerKey(nextActiveTabLink.layer).equals(
            new LayerKey(currentPathAsLink.layer),
          ) ||
          nextActiveTabLink.block !== currentPathAsLink.block
        ) {
          await navigate(
            nextActiveTabLink.variant === 'scratchpad'
              ? scratchpad({
                  server,
                  branch,
                  layer: new LayerKey(nextActiveTabLink.layer),
                  workspaceName: currentWorkspaceKey?.name,
                  block: nextActiveTabLink.block,
                })
              : configurationItem({
                  server,
                  branch,
                  block: nextActiveTabLink.block,
                  layer: new LayerKey(nextActiveTabLink.layer),
                  workspaceName: currentWorkspaceKey?.name,
                  itemId: nextActiveTabLink.id,
                  type: nextActiveTabLink.variant,
                }),
          )
        }
      }
    },
    [
      visibleTabLinks,
      updateTabLinks,
      block,
      currentPathAsLink?.id,
      currentPathAsLink?.variant,
      navigate,
      server,
      branch,
      currentWorkspaceKey,
      currentPathAsLink?.layer,
      currentPathAsLink?.block,
    ],
  )

  const closeAll = useCallback(async () => {
    updateTabLinks([])

    await navigate(
      serverPath({
        server,
        branch,
        block,
        workspaceName: currentWorkspaceKey?.name,
      }),
    )
  }, [updateTabLinks, navigate, server, branch, block, currentWorkspaceKey])

  const closeInactive = useCallback(() => {
    const nextTabs = visibleTabLinks?.filter((tabLink) => tabLink.isActive)

    // No tab is active while an item that does not exist is being viewed. There is
    // then no active tab to keep, and closing every tab is what `closeAll` is for.
    if (!nextTabs?.length) {
      return
    }

    updateTabLinks(nextTabs)
  }, [updateTabLinks, visibleTabLinks])

  return { closeTab, closeAll, closeInactive }
}
